<template>
  <div id="sidenav-collapse-main" class="w-auto collapse navbar-collapse scrollable-list">
    <ul class="navbar-nav">
      <!-- My Tools -->
      <sidenav-item :to="{ name: 'Home' }" :text="$t('sidebar.mytools')">
        <template #icon>
          <i class="material-icons-round opacity-10">star</i>
        </template>
      </sidenav-item>

      <!-- Tools -->
      <sidenav-item :to="{ name: 'Tools' }" :text="$t('sidebar.tools')">
        <template #icon>
          <i class="material-icons-round opacity-10">build</i>
        </template>
      </sidenav-item>

      <!-- Cars & heavy machinery (Admin only, configurable) -->
      <li v-if="userRole !== 'User' && !disableCars" class="nav-item">
        <sidenav-collapse collapse-ref="carsMenu" :nav-text="$t('sidebar.carsAndHeavyMachines')">
          <template #icon>
            <i class="material-icons-round opacity-10">directions_car</i>
          </template>
          <template #list>
            <ul class="nav pe-0">
              <sidenav-item :to="{ name: 'Cars' }" mini-icon="B" :text="$t('sidebar.cars')" />
              <sidenav-item :to="{ name: 'Heavy Machines' }" mini-icon="V" :text="$t('sidebar.heavyMachines')" />
            </ul>
          </template>
        </sidenav-collapse>
      </li>

      <!-- GPS Devices (Admin only, requires GPS enabled) -->
      <sidenav-item v-if="(userRole === 'Admin' || userRole === 'SuperAdmin') && enableGps" :to="{ name: 'GPS Devices' }" :text="$t('sidebar.gpsDevices')">
        <template #icon>
          <i class="material-icons-round opacity-10">gps_fixed</i>
        </template>
      </sidenav-item>

      <!-- Projects -->
      <sidenav-item :to="{ name: 'Projects' }" :text="$t('sidebar.projects')">
        <template #icon>
          <i class="material-icons-round opacity-10">list_alt</i>
        </template>
      </sidenav-item>

      <!-- Planner: all roles with the future-reservation flag (unchanged for existing
           customers), or Admins only via the EnablePlanner config -->
      <sidenav-item v-if="enableFutureReservationProjectAssignments || (enablePlanner && userRole !== 'User')" :to="{ name: 'Golf Planner' }" :text="$t('sidebar.planner')">
        <template #icon>
          <i class="material-icons-round opacity-10">calendar_month</i>
        </template>
      </sidenav-item>

      <!-- Pre-created Day Project Assignments -->
      <sidenav-item v-if="enablePreCreatedProjectAssignments" :to="{ name: 'Pre Created Day Project Assignments' }" :text="preCreatedDayProjectAssignmentsCustomName">
        <template #icon>
          <i class="material-icons-round opacity-10">bookmark</i>
        </template>
      </sidenav-item>

      <!-- Day Project Assignments -->
      <sidenav-item v-if="enableDayProjectAssignments" :to="{ name: 'Day Project Assignments' }" :text="dayProjectAssignmentsCustomName">
        <template #icon>
          <i class="material-icons-round opacity-10">menu_book</i>
        </template>
      </sidenav-item>

      <!-- End Customers (Admin only) -->
      <sidenav-item v-if="enableEndCustomers && userRole !== 'User'" :to="{ name: 'End Customers' }" :text="$t('sidebar.endCustomers')">
        <template #icon>
          <i class="material-icons-round opacity-10">person</i>
        </template>
      </sidenav-item>

      <!-- Users -->
      <sidenav-item :to="{ name: 'Users' }" :text="$t('sidebar.users')">
        <template #icon>
          <i class="material-icons-round opacity-10">groups</i>
        </template>
      </sidenav-item>

      <!-- User Profile -->
      <sidenav-item :to="{ name: 'User Profile' }" :text="$t('sidebar.userProfile')">
        <template #icon>
          <i class="material-icons-round opacity-10">badge</i>
        </template>
      </sidenav-item>

      <!-- Analytics (Admin only, submenu) -->
      <li v-if="userRole !== 'User'" class="nav-item">
        <sidenav-collapse collapse-ref="analyticsMenu" :nav-text="$t('sidebar.generalAnalytics')">
          <template #icon>
            <i class="material-icons-round opacity-10">analytics</i>
          </template>
          <template #list>
            <ul class="nav pe-0">
              <sidenav-item :to="{ name: 'General Analytics' }" mini-icon="A" :text="$t('sidebar.analytics')" />
              <sidenav-item :to="{ name: 'Warehouse Overview' }" mini-icon="W" :text="$t('sidebar.warehouseOverview')" />
              <sidenav-item :to="{ name: 'Cost Report' }" mini-icon="C" :text="$t('sidebar.costReport')" />
              <sidenav-item :to="{ name: 'Leaflet Maps' }" mini-icon="L" :text="$t('sidebar.leafletMaps')" />
            </ul>
          </template>
        </sidenav-collapse>
      </li>

      <!-- Golf (configurable) -->
      <li v-if="enableGolf" class="nav-item">
        <sidenav-collapse collapse-ref="golfMenu" :nav-text="$t('sidebar.golfDashboard')">
          <template #icon>
            <i class="material-icons-round opacity-10">sports_golf</i>
          </template>
          <template #list>
            <ul class="nav pe-0">
              <sidenav-item :to="{ name: 'Golf Dashboard' }" mini-icon="D" :text="$t('sidebar.golfDashboard')" />
              <sidenav-item :to="{ name: 'Golf Map' }" mini-icon="M" :text="$t('sidebar.golfMap')" />
              <sidenav-item v-if="userRole !== 'User'" :to="{ name: 'Golf Settings' }" mini-icon="S" :text="$t('golf.settings')" />
            </ul>
          </template>
        </sidenav-collapse>
      </li>

      <!-- Time Registration -->
      <sidenav-item v-if="enableTimeRegistration" :to="{ name: 'Time Registration' }" :text="$t('sidebar.timeRegistration') || 'Tímaskráning'">
        <template #icon>
          <i class="material-icons-round opacity-10">schedule</i>
        </template>
      </sidenav-item>
      <!-- Users have their own settings only; admins get them in the submenu below -->
      <sidenav-item v-if="userRole === 'User' || userRole === 'WarehouseUser'" :to="{ name: 'User Settings' }" :text="$t('sidebar.settings')">
        <template #icon>
          <i class="material-icons-round opacity-10">settings</i>
        </template>
      </sidenav-item>
      <!-- Settings (Admin only, submenu) -->
      <li v-if="userRole !== 'User' && userRole !== 'WarehouseUser'" class="nav-item">
        <sidenav-collapse collapse-ref="settingsMenu" :nav-text="$t('sidebar.settings')">
          <template #icon>
            <i class="material-icons-round opacity-10">settings</i>
          </template>
          <template #list>
            <ul class="nav pe-0">
              <sidenav-item :to="{ name: 'Customer Settings' }" mini-icon="C" :text="$t('sidebar.customersettings')" />
              <sidenav-item :to="{ name: 'Master Data' }" mini-icon="M" :text="$t('sidebar.masterData')" />
              <sidenav-item :to="{ name: 'User Settings' }" mini-icon="U" :text="$t('sidebar.userSettings')" />
            </ul>
          </template>
        </sidenav-collapse>
      </li>
    </ul>
  </div>
</template>

<script>
import SidenavItem from "./SidenavItem.vue";
import SidenavCollapse from "./SidenavCollapse.vue";
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { getCustomerConfig } from '../../views/Settings/customerConfigHelper';

export default {
  name: "SidenavList",
  components: {
    SidenavItem,
    SidenavCollapse,
  },
  data() {
    return {
      userRole: null,
      disableCars: false,
      enablePreCreatedProjectAssignments: false,
      enablePlanner: false,
      enableDayProjectAssignments: false,
      enableTimeRegistration: false,
      enableGolf: false,
      enableFutureReservationProjectAssignments: false,
      enableEndCustomers: false,
      enableGps: false,
      dayProjectAssignmentsCustomName: '',
      preCreatedDayProjectAssignmentsCustomName: ''
    };
  },
  async created() {
    // Get user role from token
    const token = Cookies.get('accessToken');
    if (token) {
      const decodedToken = jwtDecode(token);
      this.userRole = decodedToken.role;
    }

    // Load customer configurations
    this.loadConfigurations();
  },
  methods: {
    async loadConfigurations() {
      // Day project assignments custom name
      getCustomerConfig('DayProjectAssignmentsCustomName').then(config => {
        this.dayProjectAssignmentsCustomName = config !== '' ? config : this.$t('sidebar.todaysAssignments');
      });

      // Pre-created day project assignments custom name
      getCustomerConfig('PreCreatedDayProjectAssignmentsCustomName').then(config => {
        this.preCreatedDayProjectAssignmentsCustomName = config !== '' ? config : this.$t('sidebar.preCreatedDayProjectAssignmentsCustomName');
      });

      // Disable cars
      getCustomerConfig('DisableCars').then(config => {
        this.disableCars = config === '' ? false : config;
      });

      // Enable day project assignments
      getCustomerConfig('EnableDayProjectAssignments').then(config => {
        this.enableDayProjectAssignments = config === '' ? false : config;
      });

      // Enable future reservation project assignments
      getCustomerConfig('EnableFutureReservationProjectAssignments').then(config => {
        this.enableFutureReservationProjectAssignments = config === '' ? false : config;
      });

      // Enable pre-created project assignments (formerly EnableCalendar)
      getCustomerConfig('EnablePreCreatedProjectAssignments').then(config => {
        this.enablePreCreatedProjectAssignments = config === '' ? false : config;
      });

      // Enable planner (admin-only menu item, independent of future reservations)
      getCustomerConfig('EnablePlanner').then(config => {
        this.enablePlanner = config === '' ? false : config;
      });

      // Enable time registration
      getCustomerConfig('EnableTimeRegistration').then(config => {
        this.enableTimeRegistration = config === '' ? false : config;
      });

      // Enable end customers
      getCustomerConfig('EnableEndCustomers').then(config => {
        this.enableEndCustomers = config === true;
      });

      // Enable golf
      getCustomerConfig('EnableGolf').then(config => {
        this.enableGolf = config === '' ? false : config;
      });

      // Enable GPS (device overview)
      getCustomerConfig('EnableGPS').then(config => {
        this.enableGps = config === '' ? false : config;
      });
    },
    getRoute() {
      const routeArr = this.$route.path.split("/");
      return routeArr[1];
    }
  },
};
</script>

<style>
.scrollable-list {
  overflow-y: scroll;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.scrollable-list::-webkit-scrollbar {
  width: 0;
}
</style>
